import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onChange"];
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import isEqual from 'fast-deep-equal';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import usePrevious from '@restart/hooks/usePrevious';
import { clearTypeahead, clickOrFocusInput, getInitialState, hideMenu as _hideMenu, toggleMenu as _toggleMenu, useTypeaheadState } from './TypeaheadState';
import useValidateProps from './useValidateProps';
import { addCustomOption, defaultFilterBy, defaultSelectHint, getHintText, getInputProps, getInputText, getIsOnlyResult, getMenuProps, getOptionLabel, getStringLabelKey, getUpdatedActiveIndex, isFunction, isShown, isString, noop, uniqueId, validateSelectedPropChange } from '../utils';
import { DEFAULT_LABELKEY } from '../constants';
var defaultProps = {
  allowNew: false,
  autoFocus: false,
  caseSensitive: false,
  defaultInputValue: '',
  defaultOpen: false,
  defaultSelected: [],
  filterBy: [],
  highlightOnlyResult: false,
  ignoreDiacritics: true,
  inputProps: {},
  labelKey: DEFAULT_LABELKEY,
  minLength: 0,
  multiple: false,
  onBlur: noop,
  onFocus: noop,
  onInputChange: noop,
  onKeyDown: noop,
  onMenuToggle: noop
};

/**
 * Manually trigger the input's change event.
 * https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-onchange-event-in-react-js/46012210#46012210
 */
function triggerInputChange(input, value) {
  var inputValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
  inputValue && inputValue.set && inputValue.set.call(input, value);
  var e = new Event('input', {
    bubbles: true
  });
  input.dispatchEvent(e);
}
function useDidUpdate(props, state, setState) {
  var prevProps = usePrevious(props);
  var isInitialRender = useRef(true);
  useEffect(function () {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    validateSelectedPropChange(props.selected, prevProps === null || prevProps === void 0 ? void 0 : prevProps.selected);

    // Sync selections in state with those in props.
    if (props.selected && !isEqual(props.selected, state.selected)) {
      setState({
        selected: props.selected
      });
      if (!props.multiple) {
        setState({
          text: props.selected.length ? getOptionLabel(props.selected[0], props.labelKey) : ''
        });
      }
    }
  }, [props.labelKey, props.multiple, props.selected, prevProps === null || prevProps === void 0 ? void 0 : prevProps.selected, setState, state.selected]);
}
function useOnMenuToggle(isMenuShown, onMenuToggle) {
  var isInitialRender = useRef(true);
  useEffect(function () {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    onMenuToggle === null || onMenuToggle === void 0 || onMenuToggle(isMenuShown);
  }, [isMenuShown, onMenuToggle]);
}

/**
 * Most props used internally become "required" since they're given default
 * values.
 */

function useTypeahead(userProps, ref) {
  useValidateProps(userProps);
  var onChange = userProps.onChange,
    partialProps = _objectWithoutProperties(userProps, _excluded);
  var props = _objectSpread(_objectSpread({}, defaultProps), partialProps);
  var _useTypeaheadState = useTypeaheadState(props),
    _useTypeaheadState2 = _slicedToArray(_useTypeaheadState, 2),
    state = _useTypeaheadState2[0],
    setState = _useTypeaheadState2[1];
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    inputNode = _useState2[0],
    setInputNode = _useState2[1];
  var mergedPropsAndState = _objectSpread(_objectSpread({}, props), state);
  var filterBy = mergedPropsAndState.filterBy,
    labelKey = mergedPropsAndState.labelKey,
    options = mergedPropsAndState.options;
  var isMenuShown = isShown(mergedPropsAndState);
  var items = [];
  var itemNodes = [];
  var hintText = getHintText(_objectSpread(_objectSpread({}, mergedPropsAndState), {}, {
    isMenuShown: isMenuShown
  }));
  useDidUpdate(props, state, setState);
  useOnMenuToggle(isMenuShown, props.onMenuToggle);

  // Imperative methods
  function hideMenu() {
    setState(_hideMenu);
  }
  function blur() {
    inputNode === null || inputNode === void 0 || inputNode.blur();
  }
  function clear() {
    setState(clearTypeahead);
  }
  var focus = useCallback(function () {
    inputNode === null || inputNode === void 0 || inputNode.focus();
  }, [inputNode]);
  function getInput() {
    return inputNode;
  }
  function toggleMenu() {
    setState(_toggleMenu);
  }
  useImperativeHandle(ref, function () {
    return {
      blur: blur,
      clear: clear,
      focus: focus,
      getInput: getInput,
      hideMenu: hideMenu,
      toggleMenu: toggleMenu
    };
  });
  useEffect(function () {
    props.autoFocus && focus();
  }, [props.autoFocus, focus]);
  var results = [];
  if (isMenuShown) {
    var cb = isFunction(filterBy) ? filterBy : defaultFilterBy;
    results = options.filter(function (option) {
      return cb(option, mergedPropsAndState);
    });

    // Add the custom option if necessary.
    if (addCustomOption(results, mergedPropsAndState)) {
      results.push(_defineProperty({
        customOption: true
      }, getStringLabelKey(labelKey), state.text));
    }
  }
  var isOnlyResult = getIsOnlyResult(_objectSpread(_objectSpread({}, props), {}, {
    results: results
  }));
  function setItem(item, position, node) {
    items[position] = item;
    itemNodes[position] = node;
  }
  function onActiveIndexChange(index) {
    setState({
      activeIndex: index,
      activeItem: index >= 0 ? items[index] : undefined
    });
  }
  function onBlur(e) {
    setState(_objectSpread(_objectSpread({}, _hideMenu(state, props)), {}, {
      isFocused: false
    }), function () {
      return props.onBlur(e);
    });
  }
  function onSelect(newSelected) {
    onChange && onChange(newSelected);
  }
  function onClear() {
    inputNode && triggerInputChange(inputNode, '');
    setState(clearTypeahead, function () {
      // Change handler is automatically triggered for single selections but
      // not multi-selections.
      if (props.multiple) {
        onSelect([]);
      }
    });
  }
  function onClick(e) {
    // TODO: Make `onClick` a top-level prop?
    setState(clickOrFocusInput, function () {
      var _props$inputProps, _props$inputProps$onC;
      return (_props$inputProps = props.inputProps) === null || _props$inputProps === void 0 || (_props$inputProps$onC = _props$inputProps.onClick) === null || _props$inputProps$onC === void 0 ? void 0 : _props$inputProps$onC.call(_props$inputProps, e);
    });
  }
  function onFocus(e) {
    setState(clickOrFocusInput, function () {
      return props.onFocus(e);
    });
  }
  function onInitialItemChange(initialItem) {
    // Don't update the initial item if it hasn't changed.
    if (!isEqual(initialItem, state.initialItem)) {
      setState({
        initialItem: initialItem
      });
    }
  }
  function onInputChange(e) {
    var value = e.currentTarget.value;

    // Clear selections when the input value changes in single-select mode.
    var shouldClearSelections = state.selected.length && !props.multiple;
    var _getInitialState = getInitialState(props),
      activeIndex = _getInitialState.activeIndex,
      activeItem = _getInitialState.activeItem;
    setState({
      activeIndex: activeIndex,
      activeItem: activeItem,
      selected: shouldClearSelections ? [] : state.selected,
      showMenu: true,
      text: value
    });
    props.onInputChange(e);
    shouldClearSelections && onSelect([]);
  }
  function onAdd(option) {
    var selected;
    var selection = option;
    var text = '';

    // Add a unique id to the custom selection. Avoid doing this in `render` so
    // the id doesn't increment every time.
    if (!isString(selection) && selection.customOption) {
      selection = _objectSpread(_objectSpread({}, selection), {}, {
        id: uniqueId('new-id-')
      });
    }
    if (props.multiple) {
      // If multiple selections are allowed, add the new selection to the
      // existing selections.
      selected = [].concat(_toConsumableArray(state.selected), [selection]);
    } else {
      // If only a single selection is allowed, replace the existing selection
      // with the new one.
      selected = [selection];
      text = getOptionLabel(selection, props.labelKey);
    }
    setState(_objectSpread(_objectSpread({}, _hideMenu(state, props)), {}, {
      initialItem: selection,
      selected: selected,
      text: text
    }), function (newState) {
      return onSelect(newState.selected);
    });
  }
  function onKeyDown(e) {
    var _itemNodes$state$acti;
    props.onKeyDown(e);

    // Skip most actions when the menu is hidden.
    if (!isMenuShown) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        setState({
          showMenu: true
        });
      }
      return;
    }
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        // Prevent input cursor from going to the beginning when pressing up.
        e.preventDefault();
        onActiveIndexChange(getUpdatedActiveIndex(state.activeIndex, e.key, items));
        break;
      case 'Enter':
        // Prevent form submission while menu is open.
        e.preventDefault();
        (_itemNodes$state$acti = itemNodes[state.activeIndex]) === null || _itemNodes$state$acti === void 0 || _itemNodes$state$acti.click();
        break;
      case 'Escape':
      case 'Tab':
        // ESC simply hides the menu. TAB will blur the input and move focus to
        // the next item; hide the menu so it doesn't gain focus.
        hideMenu();
        break;
      default:
        break;
    }
    if (!state.initialItem) {
      return;
    }
    var addOnlyResult = e.key === 'Enter' && isOnlyResult;
    var shouldSelectHint = hintText && defaultSelectHint(e, props.selectHint);
    if (addOnlyResult || shouldSelectHint) {
      onAdd(state.initialItem);
    }
  }
  function onRemove(selection) {
    var selected = state.selected.filter(function (option) {
      return !isEqual(option, selection);
    });

    // Make sure the input stays focused after the item is removed.
    focus();
    setState(_objectSpread(_objectSpread({}, _hideMenu(state, props)), {}, {
      isFocused: true,
      selected: selected
    }), function () {
      return onSelect(selected);
    });
  }
  useEffect(function () {
    // Clear the initial item when there are no results.
    if (!(props.allowNew || results.length)) {
      onInitialItemChange();
    }
  });
  var context = {
    activeIndex: state.activeIndex,
    hintText: hintText,
    id: props.id,
    initialItem: state.initialItem,
    inputNode: inputNode,
    isOnlyResult: isOnlyResult,
    onInitialItemChange: onInitialItemChange,
    setItem: setItem
  };
  return _objectSpread(_objectSpread({}, mergedPropsAndState), {}, {
    context: context,
    getInputProps: getInputProps({
      activeIndex: state.activeIndex,
      id: props.id,
      inputRef: setInputNode,
      isFocused: state.isFocused,
      isMenuShown: isMenuShown,
      multiple: props.multiple,
      onBlur: onBlur,
      onChange: onInputChange,
      onClick: onClick,
      onFocus: onFocus,
      onKeyDown: onKeyDown,
      value: getInputText(mergedPropsAndState)
    }),
    getMenuProps: getMenuProps({
      id: props.id
    }),
    hideMenu: hideMenu,
    inputNode: inputNode,
    isMenuShown: isMenuShown,
    onClear: onClear,
    onHide: hideMenu,
    onItemSelect: onAdd,
    onRemove: onRemove,
    results: results,
    toggleMenu: toggleMenu
  });
}
export default useTypeahead;